import React, { createContext, useContext, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../utils/api';

// Create the auth context
const AuthContext = createContext({});

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // Get user data query
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }
      
      const response = await api.get('/auth/me');
      return response.data.data.user;
    },
    enabled: !!localStorage.getItem('token'),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }) => {
      const response = await api.post('/auth/login', { email, password });
      return response.data;
    },
    onSuccess: (data) => {
      // FIX: Estructura correcta de la respuesta del backend
      const token = data.data.token;
      const userData = data.data.user;
      
      // Store token
      localStorage.setItem('token', token);
      
      // Set user data in cache
      queryClient.setQueryData(['auth', 'user'], userData);
      
      // Force refetch to ensure user data is loaded
      refetchUser();
      
      // Show success message
      toast.success(`¡Bienvenido, ${userData.firstName || userData.name}!`);
    },
    onError: (error) => {
      const data = error.response?.data;
      let message = 'Error al iniciar sesión';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
      console.error('Login error:', error);
    },
  });

  // Google login mutation
  const googleLoginMutation = useMutation({
    mutationFn: async ({ idToken, referralCode }) => {
      console.log('🚀 Making Google login request to backend...');
      const response = await api.post('/auth/google', { idToken, referralCode });
      console.log('✅ Google login response:', response.data);
      return response.data;
    },
    onSuccess: (data) => {
      console.log('🎉 Google login successful:', data);
      const token = data.data.token;
      const userData = data.data.user;
      
      // Store token
      localStorage.setItem('token', token);
      
      // Set user data in cache
      queryClient.setQueryData(['auth', 'user'], userData);
      
      // Force refetch to ensure user data is loaded
      refetchUser();
      
      // Show success message
      const message = userData.authProvider === 'google' && !userData.googleId 
        ? 'Cuenta vinculada exitosamente'
        : `¡Bienvenido, ${userData.firstName}!`;
      toast.success(message);
    },
    onError: (error) => {
      console.error('❌ Google login error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: error.config
      });
      
      const data = error.response?.data;
      let message = 'Error al iniciar sesión con Google';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async ({ name, email, password, confirmPassword }) => {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        confirmPassword
      });
      return response.data;
    },
    onSuccess: (data) => {
      // FIX: Estructura correcta de la respuesta del backend
      const token = data.data.token;
      const userData = data.data.user;
      
      // Store token
      localStorage.setItem('token', token);
      
      // Set user data in cache
      queryClient.setQueryData(['auth', 'user'], userData);
      
      // Force refetch to ensure user data is loaded
      refetchUser();
      
      // Show success message
      toast.success('¡Cuenta creada exitosamente!');
    },
    onError: (error) => {
      // Don't show toast here - let the component handle the error
      console.error('Register error:', error);
      throw error; // Re-throw the error so the component can handle it
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/logout');
      return response.data;
    },
    onSuccess: () => {
      // Clear token and user data
      localStorage.removeItem('token');
      queryClient.clear();
      
      // Show success message
      toast.success('Sesión cerrada exitosamente');
    },
    onError: (error) => {
      // Even if logout fails on server, clear local data
      localStorage.removeItem('token');
      queryClient.clear();
      
      const data = error.response?.data;
      let message = 'Error al cerrar sesión';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
      console.error('Logout error:', error);
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: async ({ email }) => {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Email de recuperación enviado');
    },
    onError: (error) => {
      const data = error.response?.data;
      let message = 'Error al enviar email de recuperación';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ token, password, confirmPassword }) => {
      const response = await api.put(`/auth/reset-password/${token}`, {
        password,
        confirmPassword
      });
      return response.data;
    },
    onSuccess: (data) => {
      // If reset includes a token, store it
      if (data.data?.token) {
        localStorage.setItem('token', data.data.token);
        queryClient.setQueryData(['auth', 'user'], data.data.user);
        refetchUser();
      }
      
      toast.success('Contraseña cambiada exitosamente');
    },
    onError: (error) => {
      const data = error.response?.data;
      let message = 'Error al cambiar contraseña';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async ({ currentPassword, password, confirmPassword }) => {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        password,
        confirmPassword
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Update token if provided
      if (data.data?.token) {
        localStorage.setItem('token', data.data.token);
      }
      
      toast.success('Contraseña cambiada exitosamente');
    },
    onError: (error) => {
      const data = error.response?.data;
      let message = 'Error al cambiar contraseña';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
    },
  });

  // Verify email mutation
  const verifyEmailMutation = useMutation({
    mutationFn: async ({ token }) => {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Email verificado exitosamente');
      // Refetch user to update verification status
      refetchUser();
    },
    onError: (error) => {
      const data = error.response?.data;
      let message = 'Error al verificar email';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
    },
  });

  // Resend verification mutation
  const resendVerificationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/resend-verification');
      return response.data;
    },
    onSuccess: () => {
      toast.success('Email de verificación enviado');
    },
    onError: (error) => {
      const data = error.response?.data;
      let message = 'Error al enviar email de verificación';
      
      if (data?.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.msg || err.message).join(', ');
      } else if (data?.message) {
        message = data.message;
      }
      
      toast.error(message);
    },
  });

  // Logout function
  const logout = () => {
    logoutMutation.mutate();
  };

  // Login function
  const login = (credentials) => {
    return loginMutation.mutateAsync(credentials);
  };

  // Google login function
  const loginWithGoogle = (googleData) => {
    console.log('🔐 Google login data received:', googleData);
    
    // Google sends { credential: "token" } but backend expects { idToken: "token" }
    const data = {
      idToken: googleData.credential || googleData.idToken,
      referralCode: googleData.referralCode
    };
    
    console.log('📤 Sending to backend:', {
      idToken: data.idToken ? `${data.idToken.substring(0, 20)}...` : 'undefined',
      referralCode: data.referralCode
    });
    
    return googleLoginMutation.mutateAsync(data);
  };

  // Register function
  const register = async (userData) => {
    try {
      return await registerMutation.mutateAsync(userData);
    } catch (error) {
      // Re-throw the error so the component can handle it
      throw error;
    }
  };

  // Forgot password function
  const forgotPassword = (email) => {
    return forgotPasswordMutation.mutateAsync({ email });
  };

  // Reset password function
  const resetPassword = (token, passwords) => {
    return resetPasswordMutation.mutateAsync({ token, ...passwords });
  };

  // Change password function
  const changePassword = (passwords) => {
    return changePasswordMutation.mutateAsync(passwords);
  };

  // Verify email function
  const verifyEmail = (token) => {
    return verifyEmailMutation.mutateAsync({ token });
  };

  // Resend verification email function
  const resendVerification = () => {
    return resendVerificationMutation.mutateAsync();
  };

  // Check if user is authenticated
  const isAuthenticated = !!user && !!localStorage.getItem('token');

  // Check if user has verified email
  const isEmailVerified = user?.isEmailVerified || false;

  // Check if user is premium
  const isPremium = user?.plan === 'premium' || user?.plan === 'enterprise';

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || hasRole('admin');
  };

  // Loading state
  const isLoading = isLoadingUser || 
    loginMutation.isPending || 
    googleLoginMutation.isPending ||
    registerMutation.isPending || 
    logoutMutation.isPending;

  // Error handling effect
  useEffect(() => {
    if (userError && userError.message !== 'No token found') {
      // If there's an auth error, clear the invalid token
      localStorage.removeItem('token');
      queryClient.clear();
    }
  }, [userError, queryClient]);

  // Debug: Log auth state changes
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔐 Auth State:', {
        user: user ? `${user.firstName} ${user.lastName}` : null,
        isAuthenticated,
        hasToken: !!localStorage.getItem('token'),
        isLoading,
        userError: userError?.message
      });
    }
  }, [user, isAuthenticated, isLoading, userError]);

  // Context value
  const value = {
    // User data
    user,
    isAuthenticated,
    isEmailVerified,
    isPremium,
    isLoading,

    // Auth functions
    login,
    loginWithGoogle,
    register,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    verifyEmail,
    resendVerification,
    refetchUser,

    // Utility functions
    hasRole,
    hasPermission,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingInWithGoogle: googleLoginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isForgettingPassword: forgotPasswordMutation.isPending,
    isResettingPassword: resetPasswordMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isVerifyingEmail: verifyEmailMutation.isPending,
    isResendingVerification: resendVerificationMutation.isPending,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 